import { useId, type ReactNode } from 'react';
import { Segmented } from '../../components/shared';
import { tCalendar } from '../../lib/calendar-i18n';
import { tCalCreate, type CalendarCreateI18nKey } from '../../lib/calendar-create-i18n';
import {
  RECUR_TABS, WEEKDAY_CODES, WEEKDAY_KEYS, localDateOf, turnOnWeekly,
  monthlyByDateLabel, monthlyByWeekdayLabel, yearlyByDateLabel, yearlyByWeekdayLabel,
  type RecurState, type WeekdayCode,
} from './recurrence-form';
import './RecurrenceSettings.css';

/**
 * 반복 설정 — 4탭(매일·매주·매월·매년) · 매주 요일 고르기 · 매월/매년 두 방식 (2026-10-13 이식).
 *
 * 출처: 옛 가지 ee33d27d7(4탭 · 요일) · 21f8b4e23(몇째 주 요일) · ec36d8a56(말일) · e9255f050(수정 화면).
 * ⭐ 상태는 «부모»가 든다(`RecurState`) — 저장 셈(`recurrenceInputFromState`)이 같은 값을 읽어야 하기 때문이다.
 * ⭐ 탭과 방식은 정본 B6 `Segmented`(네이티브 라디오)로 — 묶음 안은 ←→ 로 옮긴다.
 * ⚠ 매월·매년 방식의 글자는 «시작 날짜»에서 나온다 — 시작을 바꾸면 라벨도 따라 바뀐다(「매월 셋째 화요일」).
 */
export interface RecurrenceSettingsProps {
  value: RecurState;
  onChange: (next: RecurState) => void;
  /** 시작(`YYYY-MM-DDTHH:MM`, 현지) — 매주 기본 요일 · 매월/매년 라벨이 여기서 나온다. */
  startLocal: string;
  disabled?: boolean;
  /** 패널 위 한 줄(수정 화면의 「이 회차부터 뒤로만」 등). */
  hint?: ReactNode;
  /**
   * ⭐ 2026-10-14 (반복 규칙 E-3) — 이미 「횟수」(COUNT)로 저장된 일정을 열었다. 그 일정에만 옛 「횟수」 선택지를 남긴다
   *   (새로 고르는 선택지는 계속·기간·날짜). 미지정이어도 지금 값이 'count' 면 보인다.
   */
  legacyCount?: boolean;
}

export function RecurrenceSettings({ value, onChange, startLocal, disabled = false, hint, legacyCount = false }: RecurrenceSettingsProps) {
  const uid = useId();
  const on = value.freq !== '';
  const set = (patch: Partial<RecurState>) => onChange({ ...value, ...patch });

  function selectTab(f: (typeof RECUR_TABS)[number]) {
    // 매주로 처음 들어오면 시작 요일을 미리 켠다 — 일정 창의 「반복」 체크와 같은 규칙(recurrence-form `turnOnWeekly`).
    if (f === 'WEEKLY') onChange(turnOnWeekly(value, startLocal));
    else set({ freq: f });
  }

  function toggleDay(code: WeekdayCode) {
    const has = value.byDay.includes(code);
    // ⚠ 마지막 하나는 못 끈다 — 다 끄면 서버가 시작 요일로 채워 「고른 것과 다른」 결과가 된다.
    if (has && value.byDay.length === 1) return;
    set({ byDay: has ? value.byDay.filter((c) => c !== code) : [...value.byDay, code] });
  }

  /*
   * 「반복」 켜고 끄기는 이 부품 밖(일정 창 — 「하루종일 · 반복」 한 줄)에 있다 (2026-10-14).
   *   이 부품은 켜져 있을 때 패널만 그린다(종전의 자체 체크박스·hideToggle·lockOn 은 부르는 곳이 없어 걷었다).
   */
  if (!on) return null;

  return (
    <div className="calendar-modal-field cal-recur">
      <div className="calendar-recur-options cal-recur__panel">
        {hint && <p className="calendar-modal-hint">{hint}</p>}
        <Segmented
          className="cal-recur__tabs"
          ariaLabel={tCalCreate('rc.freq_label')}
          value={value.freq}
          onChange={(v) => selectTab(v as (typeof RECUR_TABS)[number])}
          disabled={disabled}
          size="sm"
          options={RECUR_TABS.map((f) => ({ value: f, label: tCalCreate(`rc.tab_${f.toLowerCase()}` as CalendarCreateI18nKey) }))}
        />

        <div className="calendar-recur-row">
          <label htmlFor={`${uid}-interval`}>{tCalendar('calendar.recur.interval_label')}</label>
          <input
            id={`${uid}-interval`}
            type="number" min={1} max={365}
            value={value.interval}
            onChange={(e) => set({ interval: Math.max(1, Math.min(365, Number(e.target.value) || 1)) })}
            disabled={disabled}
            className="calendar-recur-interval"
          />
          <span>{tCalCreate(`rc.unit_${value.freq.toLowerCase()}` as CalendarCreateI18nKey)}</span>
        </div>

        {value.freq === 'WEEKLY' && (
          <div className="cal-recur__days" role="group" aria-label={tCalCreate('rc.weekdays_label')}>
            {WEEKDAY_CODES.map((code, i) => {
              const checked = value.byDay.includes(code);
              return (
                <label key={code} className={`cal-recur__day${checked ? ' is-on' : ''}`}>
                  <input
                    type="checkbox"
                    className="cal-recur__day-input"
                    checked={checked}
                    onChange={() => toggleDay(code)}
                    disabled={disabled}
                  />
                  <span>{tCalCreate(`rc.weekday.${WEEKDAY_KEYS[i]}` as CalendarCreateI18nKey)}</span>
                </label>
              );
            })}
          </div>
        )}

        {(value.freq === 'MONTHLY' || value.freq === 'YEARLY') && (
          <Segmented
            className="cal-recur__mode"
            ariaLabel={tCalCreate('rc.mode_label')}
            value={value.byWeekday ? 'weekday' : 'date'}
            onChange={(v) => set({ byWeekday: v === 'weekday' })}
            disabled={disabled}
            size="sm"
            options={[
              { value: 'date', label: value.freq === 'MONTHLY' ? monthlyByDateLabel(startLocal) : yearlyByDateLabel(startLocal) },
              { value: 'weekday', label: value.freq === 'MONTHLY' ? monthlyByWeekdayLabel(startLocal) : yearlyByWeekdayLabel(startLocal) },
            ]}
          />
        )}

        <div className="calendar-recur-row">
          <label htmlFor={`${uid}-end`}>{tCalendar('calendar.recur.end_label')}</label>
          <select
            id={`${uid}-end`}
            value={value.end}
            onChange={(e) => set({ end: e.target.value as RecurState['end'] })}
            disabled={disabled}
          >
            <option value="never">{tCalendar('calendar.recur.end_never')}</option>
            {/* ⭐ 2026-10-14 (E-3) — 「횟수」 대신 「기간」. 횟수는 이미 횟수로 저장된 일정에만 남는다. */}
            <option value="period">{tCalendar('calendar.recur.end_period')}</option>
            {(legacyCount || value.end === 'count') && (
              <option value="count">{tCalendar('calendar.recur.end_count')}</option>
            )}
            <option value="until">{tCalendar('calendar.recur.end_until')}</option>
          </select>
          {value.end === 'period' && (
            <>
              <input
                type="number" min={1} max={999}
                aria-label={tCalendar('calendar.recur.end_period')}
                value={value.period}
                onChange={(e) => set({ period: Math.max(1, Math.min(999, Number(e.target.value) || 1)) })}
                disabled={disabled}
                className="calendar-recur-count"
              />
              <span>{tCalCreate(`rc.period_unit_${value.freq.toLowerCase()}` as CalendarCreateI18nKey)}</span>
            </>
          )}
          {value.end === 'count' && (
            <input
              type="number" min={1} max={1000}
              aria-label={tCalendar('calendar.recur.end_count')}
              value={value.count}
              onChange={(e) => set({ count: Math.max(1, Math.min(1000, Number(e.target.value) || 1)) })}
              disabled={disabled}
              className="calendar-recur-count"
            />
          )}
          {value.end === 'until' && (
            <input
              type="date"
              aria-label={tCalendar('calendar.recur.end_until')}
              /* ⭐ 2026-10-15 (E2E #5) — 시작 날짜보다 앞의 종료는 고를 수 없다(서버도 회차 0개를 거절한다). */
              min={localDateOf(startLocal) ?? undefined}
              value={value.until}
              onChange={(e) => set({ until: e.target.value })}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    </div>
  );
}
